'use client';

/**
 * SysObeyaTeamComparisonCard — side-by-side comparison of a
 * traditional agile team vs 1 Person + AI. Slices `response.totals`
 * + `response.config`. Includes the Total Savings conclusion band.
 *
 * Typography from `useTypographyBundle('canvas_dashboard')`:
 *   header        = card title (20px font-semibold)
 *   subtitle      = card subtitle (14px muted)
 *   section_label = team panel labels + savings band header (16px;
 *                   primary tint applied locally on AI side / band)
 *   body          = metric row labels and values (14px)
 *   metadata      = savings-row labels (12px muted)
 *   amount        = savings values (36px primary bold) — derived
 *                   conclusion treatment
 *
 * Container chrome — team panels with `rounded-xl p-5`, primary-tinted
 * icon chips, the Total Savings band with `bg-primary/5 border-primary/20`
 * — lives here as render concerns per the mode7 doctrine.
 *
 * Provenance rule: derived conclusions (Total Savings) get the tinted
 * band + primary `amount`; measured facts (KPIs) get plain cards.
 * Same provenance distinction the legacy widget encoded implicitly.
 *
 * @see klyde_edge/ui_config/labels/system_obeya.py
 */

import { useMemo } from 'react';
import { Users, Sparkles, Clock, Zap, DollarSign, TrendingDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { type TypographyBundleSlots } from '@/lib/design/typography';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { useSystemObeyaView, type SystemObeyaLabels } from './use-system-obeya-view';

interface Props {
  view_name?: string;
}

type TeamSide = 'traditional' | 'ai';
type MetricFormat = 'people' | 'calendar_days' | 'hours' | 'currency';

interface MetricDef {
  key: string;
  label_key: string;
  format: MetricFormat;
  icon: LucideIcon;
}

interface SavingsMetricDef {
  key: string;
  label_key: string;
}

const METRICS: MetricDef[] = [
  { key: 'team_size',   label_key: 'mod_sum_metric_team_size',   format: 'people',        icon: Users      },
  { key: 'duration',    label_key: 'mod_sum_metric_duration',    format: 'calendar_days', icon: Clock      },
  { key: 'total_hours', label_key: 'mod_sum_metric_total_hours', format: 'hours',         icon: Zap        },
  { key: 'est_cost',    label_key: 'mod_sum_metric_est_cost',    format: 'currency',      icon: DollarSign },
];

const SAVINGS_METRICS: SavingsMetricDef[] = [
  { key: 'team_members', label_key: 'mod_sum_savings_team_members' },
  { key: 'hours_saved',  label_key: 'mod_sum_savings_hours_saved'  },
  { key: 'cost_savings', label_key: 'mod_sum_savings_cost_savings' },
];

export function SysObeyaTeamComparisonCard({ view_name = 'module_summary' }: Props) {
  const { data, isLoading, queryKey } = useSystemObeyaView(view_name);
  const T = useCardTypography();
  const L: SystemObeyaLabels = data?.labels ?? {};

  // Derive everything UNCONDITIONALLY so hook order stays stable —
  // the early return moves down past the useCardExport call. When
  // data isn't loaded, the values resolve to empty / dash and the
  // export memo gracefully produces an empty markdown.
  const t = data?.totals ?? {};
  const c = data?.config ?? {};
  const dash = L.dash_placeholder ?? '—';

  // Currency is data, not chrome. The BFF ships `config.currency`
  // (ISO-4217) alongside the cost figures because there's no
  // exchange-rate engine here — painting a locale-ish "$" would
  // imply a conversion that isn't happening. Renderer just appends
  // the explicit code.
  const currency = typeof c.currency === 'string' ? c.currency : '';
  const savingsValues: Record<string, string> = {
    team_members:
      c.traditional_team_size != null && c.ai_team_size != null
        ? `${L.sign_decrement ?? ''}${(c.traditional_team_size as number) - (c.ai_team_size as number)}`
        : dash,
    hours_saved:
      t.total_saved_hours != null
        ? fmtInt(Number(t.total_saved_hours))
        : dash,
    cost_savings:
      t.savings_usd != null ? formatCurrency(Number(t.savings_usd), currency) : dash,
  };

  // Card export — two team panels + savings band as markdown.
  const compTitle = L.mod_sum_team_comp_title ?? '';
  useCardExport(useMemo(() => {
    const formatPanel = (side: TeamSide, title: string) => {
      const lines = METRICS.map((m) => {
        const v = pickMetricValue(side, m.key, t, c);
        return `- ${L[m.label_key] ?? m.key}: ${formatMetric(v, m.format, L, currency)}`;
      });
      return `### ${title}\n${lines.join('\n')}`;
    };
    const tradPanel = formatPanel('traditional', L.mod_sum_team_traditional_label ?? 'Traditional');
    const aiPanel = formatPanel('ai', L.mod_sum_team_ai_label ?? 'AI');
    const savings = SAVINGS_METRICS
      .map((m) => `- ${L[m.label_key] ?? m.key}: ${savingsValues[m.key] ?? dash}`)
      .join('\n');
    const md = [
      tradPanel,
      aiPanel,
      `### ${L.mod_sum_savings_header ?? 'Total Savings'}\n${savings}`,
    ].join('\n\n');
    // Scene json mirrors what each row actually PAINTS — formatted
    // strings with units, currency, sign treatment ("8 people",
    // "$2,630,400", "-7", "$2.30M"). The raw numerics + the savings
    // producer's semantic field names travel in the capture
    // envelope's `payloads` section, so the pair stays checkable:
    //   format(payload.totals.trad_cost_usd, labels) == scene.team.est_cost
    const teamSlice = (side: TeamSide) => {
      const out: Record<string, string> = {};
      for (const m of METRICS) {
        const v = pickMetricValue(side, m.key, t, c);
        out[m.key] = formatMetric(v, m.format, L, currency);
      }
      return out;
    };
    return {
      label: compTitle,
      text: md.replace(/^#+ /gm, '').replace(/^- /gm, '  '),
      markdown: md,
      json: {
        teams: {
          traditional: teamSlice('traditional'),
          ai: teamSlice('ai'),
        },
        savings: {
          team_members: savingsValues.team_members,
          hours_saved: savingsValues.hours_saved,
          cost_savings: savingsValues.cost_savings,
        },
      },
      content_key: 'sys_obeya_team_comparison',
      surface: 'canvas',
      // Status = paintable content, not the query flag.
      status: (data ? 'loaded' : 'loading') as 'loading' | 'loaded',
      query_key: queryKey,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(t), JSON.stringify(c), JSON.stringify(L), JSON.stringify(savingsValues), dash, compTitle, data, isLoading, queryKey]));

  // Early return MUST come after all hook calls — Rules of Hooks.
  if (isLoading || !data) {
    return (
      <CardShell>
        <div className="p-6">
          <div className={T.body}>{L.loading_message ?? ''}</div>
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div className="p-6 flex flex-col h-full">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            <h2 className={T.header}>{L.mod_sum_team_comp_title ?? ''}</h2>
          </div>
          <p className={cn(T.subtitle, 'mt-1')}>{L.mod_sum_team_comp_subtitle ?? ''}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <TeamPanel
            side="traditional"
            icon={Users}
            title={L.mod_sum_team_traditional_label ?? ''}
            totals={t}
            config={c}
            T={T}
            L={L}
          />
          <TeamPanel
            side="ai"
            icon={Sparkles}
            title={L.mod_sum_team_ai_label ?? ''}
            totals={t}
            config={c}
            T={T}
            L={L}
            highlight
          />
        </div>

        <div className="rounded-xl bg-primary/5 border border-primary/20 p-5 mt-auto">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown className="w-5 h-5 text-primary" />
            <h3 className={cn(T.section_label, 'text-primary')}>{L.mod_sum_savings_header ?? ''}</h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {SAVINGS_METRICS.map((m) => (
              <div key={m.key}>
                <p className={T.amount}>{savingsValues[m.key] ?? dash}</p>
                <p className={cn(T.metadata, 'mt-1')}>{L[m.label_key] ?? ''}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </CardShell>
  );
}

// ─── TeamPanel — one team's metrics, with optional primary tint ────

function TeamPanel({
  side,
  icon: Icon,
  title,
  totals,
  config,
  T,
  L,
  highlight,
}: {
  side: TeamSide;
  icon: LucideIcon;
  title: string;
  totals: Record<string, unknown>;
  config: Record<string, unknown>;
  T: TypographyBundleSlots;
  L: SystemObeyaLabels;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl p-5 border transition-all',
        highlight
          ? 'border-primary/50 bg-primary/5'
          : 'border-border/50 bg-muted/30',
      )}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={cn(
            'p-2 rounded-lg',
            highlight ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon className="w-5 h-5" />
        </div>
        <h3 className={cn(T.section_label, highlight && 'text-primary')}>{title}</h3>
      </div>
      <dl className="space-y-3">
        {METRICS.map((m) => {
          const value = pickMetricValue(side, m.key, totals, config);
          const cur = typeof config.currency === 'string' ? config.currency : '';
          const formatted = formatMetric(value, m.format, L, cur);
          const isCost = m.key === 'est_cost';
          const MetricIcon = m.icon;
          return (
            <div key={m.key} className="flex items-center justify-between gap-3">
              <dt className={cn(T.body, 'text-muted-foreground flex items-center gap-2')}>
                <MetricIcon className="w-4 h-4" />
                {L[m.label_key] ?? ''}
              </dt>
              <dd
                className={cn(
                  T.body,
                  'font-mono font-medium tabular-nums',
                  isCost && highlight && 'text-primary',
                )}
              >
                {formatted}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

// ─── Value lookup ───────────────────────────────────────────────────

function pickMetricValue(
  side: TeamSide,
  key: string,
  totals: Record<string, unknown>,
  config: Record<string, unknown>,
): number | null {
  const num = (v: unknown): number | null =>
    typeof v === 'number' ? v : v == null ? null : Number(v);

  switch (key) {
    case 'team_size':
      return num(side === 'traditional' ? config.traditional_team_size : config.ai_team_size);
    case 'duration':
      return num(totals.calendar_days);
    case 'total_hours':
      return num(side === 'traditional' ? totals.total_trad_hours : totals.total_ai_hours);
    case 'est_cost':
      return num(side === 'traditional' ? totals.trad_cost_usd : totals.ai_cost_usd);
    default:
      return null;
  }
}

function formatMetric(
  value: number | null,
  format: MetricFormat,
  L: SystemObeyaLabels,
  currency: string,
): string {
  const dash = L.dash_placeholder ?? '—';
  if (value == null) return dash;
  switch (format) {
    case 'people':
      return `${value} ${value === 1 ? (L.unit_person ?? '') : (L.unit_people ?? '')}`.trim();
    case 'calendar_days':
      return `${fmtInt(value)} ${L.unit_calendar_days ?? ''}`.trim();
    case 'hours':
      return fmtInt(value);
    case 'currency':
      return formatCurrency(value, currency);
  }
}

// ─── Format helpers ─────────────────────────────────────────────────

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-border bg-card shadow-sm h-full overflow-hidden flex flex-col">{children}</div>;
}
function fmtInt(n: number): string {
  return Math.round(n).toLocaleString();
}
// Render a money value with its currency code beside it
// ("2,630,400 USD"). No locale symbol, no K/M compact form — those
// would imply formatting context (currency conversion, locale
// thousands convention) that this app doesn't actually perform.
// Currency is part of the data; the renderer just states it.
function formatCurrency(n: number, currency: string): string {
  const num = fmtInt(n);
  return currency ? `${num} ${currency}` : num;
}
